/**
 * Aggregates last blocks from different networks
 */
import { IDexTrade, ITicker } from '@kattana-io/common';
import { wsEndpoint } from './endpoints';
import Centrifuge, { Subscription } from 'centrifuge';
import eventEmitter from './eventEmitter';
import { getLiveTradesEventName } from '../helpers/events-builder';

interface ICallbacksProps {
  trade: (trade: IDexTrade) => void;
  newpairs: (message: string) => void;
  priceAlerts: (data: any) => void;
  limitOrders: (data: any) => void;
}

export interface IGasState {
    FastGasPrice: string
    LastBlock: string
    ProposeGasPrice: string
    SafeGasPrice: string
}
interface ITradeData {
    amountIn: string; // "1002888007142555399"
    amountOut: string; // "1792722382"
    blocknumber: number; // 12000786
    date: string; // "2021-03-08 23:15:01"
    pair: string; // "0x0d4a11d5EEaaC28EC3F61d100daF4d40471f1852"
    price: string; // 1787.5599
    priceUSD: string;
    protocol: string; // "uniswapV2"
    tx: string; // "0xdda30da43b4f583d2f6c75e7797327a9b889cca16657d400c20462f3632ee79e"
    wallet: string; // "0x000000000000000000000000f06371cfecd87065ec3aaf792252fe02305f0cb2"
    timestamp: number;
    bot: boolean
    buy: boolean
    inch: boolean
}
interface IMessageTradePayload {
    data: IDexTrade;
}

const dispatchNewEventForTrades = (trade: IDexTrade, chain: string, base: string, quote: string) => {
  const eventName = getLiveTradesEventName(chain, base, quote);
  eventEmitter.emit(eventName, { trade });
};

class BlocksSubscription {

    interval: any;
    callbacks: ICallbacksProps;
    centrifuge: Centrifuge | undefined;
    tradeSubscriptions: { [hash: string]: { count: number, sub?: Subscription } };
    priceAlertSubscription?: Subscription;
    limitOrdersSubscription?: Subscription;

    constructor(callbacks: ICallbacksProps) {
      this.callbacks = callbacks;
      this.onConnectWs();
      this.tradeSubscriptions = {};
    }

    private onConnectWs = () => {
      this.centrifuge = new Centrifuge(wsEndpoint, {
        websocket: WebSocket
      });
      this.centrifuge.connect();
      this.centrifuge.on('disconnect', (disconnectCtx) => {
        console.error('disconnected', disconnectCtx);
      });
      // this.centrifuge.subscribe("price", (message: any) => {
      //     // console.log(message.data)
      // })
      // this.centrifuge.subscribe("newpairs", (message: any) => {
      //     const { pair, protocol } = message.data
      //     const msg = `A new pair ${pair} was created on ${protocol}`
      //     this.callbacks.newpairs(msg)
      //     console.log(msg)
      // })
    }

    // private cancelPreviousSubscriptions() {
    //   this.subscriptions.forEach((subscription: any) => {
    //     subscription.unsubscribe();
    //   });
    //   this.subscriptions = [];
    // }

    private createPriceAlertSubscription(wallet: string) {
      const alertsSubscription = `notifications/${wallet}`;

      const sub = this.centrifuge?.subscribe(alertsSubscription, (message: any) => {
        this.callbacks.priceAlerts(message.data);
      });

      return sub;
    }

    private createLimitOrdersSubscription(wallet: string) {
      const ordersSubscription = `limit_orders/${wallet}`;
      const sub = this.centrifuge?.subscribe(ordersSubscription, (message: any) => {
        this.callbacks.limitOrders(message.data);
      });

      return sub;
    }

    /**
     * Create and return subscription object
     * @param protocol
     * @param address
     * @param exchange
     * @param base
     * @param quote
     * @returns
     */
    private createTradeSubscription = (chain: string, address: string, base: string, quote: string): Subscription | undefined => {
      const tradesSubscription = `trades/${chain}/${address}`;
      const sub = this.centrifuge?.subscribe(tradesSubscription, (message: any) => {
        /**
         * technical debt:
         * This is the only point to receive live trades
         * So in order to sort this out and avoid this kind of convertation
         * we can do necessary preparation on the backend
         */
        const liveTrade: IDexTrade = {
          ...message.data,
          timestamp: Number(new Date(message.data.date)) / 1000,
          price_a: Number(message.data.pricea),
          price_a_usd: Number(message.data.pricea_usd),
          price_b: Number(message.data.priceb),
          price_b_usd: Number(message.data.priceb_usd),
          buy: message.data.buy ? 1 : 0
        };

        dispatchNewEventForTrades(liveTrade, chain, base, quote);
      });

      return sub;
    }
    /**
     * subscribeToPriceAlerts
     */
    public subscribeToPriceAlerts(wallet: string) {
      if (this.priceAlertSubscription) {
        this.priceAlertSubscription.unsubscribe();
      }

      const sub = this.createPriceAlertSubscription(wallet);
      this.priceAlertSubscription = sub;
    }

    /**
     * subscribeToLimitOrders
     */
    public subscribeToLimitOrders(wallet: string) {
      if (this.limitOrdersSubscription) {
        this.limitOrdersSubscription.unsubscribe();
      }

      const sub = this.createLimitOrdersSubscription(wallet);
      this.limitOrdersSubscription = sub;
    }

    /**
     * unsubscribeFromPriceAlerts
     */
    public unsubscribeFromPriceAlerts() {
      this.priceAlertSubscription?.unsubscribe();
      delete this.priceAlertSubscription;
    }

    /**
     * unsubscribeFromLimitOrders
     */
    public unsubscribeFromLimitOrders() {
      this.limitOrdersSubscription?.unsubscribe();
      delete this.limitOrdersSubscription;
    }

    /**
     * Unsubscribe from trades
     */
    public unsubscribeFromLiveTrades(curHash: string) {
      if (this.tradeSubscriptions[curHash].count > 1) {
        this.tradeSubscriptions[curHash].count -= 1;
      } else {
        this.tradeSubscriptions[curHash].sub?.unsubscribe();
        delete this.tradeSubscriptions[curHash];
      }
    }
    /**
     * Subscribe to trades
     */
    public subscribeToLiveTrades(curHash: string, ticker: ITicker) {
      if (!this.tradeSubscriptions[curHash]) {
        const { name, address, chain } = ticker;
        const [base, quote] = name.split('/');
        const sub = this.createTradeSubscription(chain, address, base, quote);
        this.tradeSubscriptions[curHash] = { count: 1, sub };
      } else {
        this.tradeSubscriptions[curHash].count += 1;
      }
    }
    /**
     * Create cex last price subscription
     * @param id
     */
    public subscribeCexLastPrice = (subName: string) => {
      let sub = this.centrifuge?.getSub(subName);
      if (!sub) {
        sub = this.centrifuge?.subscribe(subName, (message: any) => {
          eventEmitter.emit(subName, { price: message.data.price });
        });
      }

      return () => {
        const listenersCount = eventEmitter.listeners(subName).length;

        if (listenersCount < 1) {
          sub?.unsubscribe();
        }
      };
    }
}

export default BlocksSubscription;